/**
 * Google Sheets connection card for the Connections page.
 *
 * Read-only, and the copy says so plainly — a tenant handing over a
 * spreadsheet full of leads deserves to know nothing is ever written back.
 *
 * Three ideas drive the layout:
 *
 *   A grant belongs to a Google ACCOUNT, so "connected" is one line at the top,
 *   never a per-sheet state. That mirrors the HubSpot card's header exactly,
 *   using the same StatusPill and the same relative-time format.
 *
 *   Sheets are grouped by WORKBOOK, with each tab as a row beneath it. This is
 *   the shape of the thing being described: a spreadsheet is one file with
 *   several tabs, and a tenant thinks "my leads workbook", not "twelve
 *   unrelated sources". It also puts every connected tab of a file next to
 *   each other, so the tabs that are NOT connected are visible by their
 *   absence — the exact failure the file uploader on the Leads page still has,
 *   where only the first sheet is ever read and nothing says so.
 *
 *   Each tab gets one plain-language state line by default, with the run-by-run
 *   history one deliberate click away. Same progressive disclosure the HubSpot
 *   card uses, for the same reason: a history feed is a debugging surface, not
 *   a status surface.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  DeleteOutline,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  LinkOff,
  OpenInNew,
  Replay,
  Sync,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTerminology } from '@/hooks/useTerminology';
import adminApi, {
  GoogleSheetsConnectorStatus,
  GoogleSheetSource,
  GoogleSheetTab,
  GoogleSheetSyncMode,
  ConnectorSyncRun,
} from '@/services/admin.api';
import { useGooglePicker } from '@/hooks/useGooglePicker';
import GoogleSheetsIcon, {
  GOOGLE_SHEETS_GREEN,
  GOOGLE_SHEETS_GREEN_DARK,
} from '@/components/icons/GoogleSheetsIcon';
import {
  StatusPill,
  TRIGGER_CHIP_SX,
  TRIGGER_LABEL,
  timeAgo,
  formatCount,
  CONNECTOR_POLL_MS,
} from './connectorUi';

/** Mirrors Leads.tsx's own import targets so a synced column and an uploaded
 *  column offer the tenant exactly the same vocabulary. */
const CUSTOM_FIELD_SELECT_VALUE = '__custom__';
const CUSTOM_FIELD_MAPPING_PREFIX = 'custom:';

const TARGET_FIELD_OPTIONS = [
  { value: '', label: "Don't import" },
  { value: 'customerName', label: 'Name' },
  { value: 'customerPhone', label: 'Phone' },
  { value: 'customerEmail', label: 'Email' },
  { value: 'customerCompany', label: 'Company' },
  { value: 'customerCountry', label: 'Country' },
  { value: 'title', label: 'Enquiry Title' },
  { value: 'description', label: 'Enquiry Description' },
  { value: 'interest', label: 'Interest' },
  { value: 'estimatedValue', label: 'Estimated Value' },
  { value: 'budget', label: 'Budget' },
  { value: 'notes', label: 'Notes' },
  { value: 'tags', label: 'Tags' },
  { value: CUSTOM_FIELD_SELECT_VALUE, label: 'Custom field…' },
];

const normalizeHeader = (header: string): string =>
  header.toLowerCase().trim().replace(/[^a-z0-9]+/g, '');

/** The alias table Leads.tsx uses for uploaded files, plus the column names
 *  Meta's own Lead Ads export writes — a sheet fed by Meta should arrive
 *  almost fully mapped before the tenant touches anything. */
const FIELD_ALIASES: Record<string, string> = {
  name: 'customerName',
  fullname: 'customerName',
  customername: 'customerName',
  phone: 'customerPhone',
  phonenumber: 'customerPhone',
  mobile: 'customerPhone',
  mobilenumber: 'customerPhone',
  customerphone: 'customerPhone',
  whatsapp: 'customerPhone',
  whatsappnumber: 'customerPhone',
  email: 'customerEmail',
  emailaddress: 'customerEmail',
  customeremail: 'customerEmail',
  company: 'customerCompany',
  companyname: 'customerCompany',
  organization: 'customerCompany',
  customercompany: 'customerCompany',
  country: 'customerCountry',
  customercountry: 'customerCountry',
  title: 'title',
  enquirytitle: 'title',
  subject: 'title',
  description: 'description',
  enquirydescription: 'description',
  interest: 'interest',
  value: 'estimatedValue',
  estimatedvalue: 'estimatedValue',
  budget: 'budget',
  notes: 'notes',
  tags: 'tags',
  tag: 'tags',
  segment: 'tags',
};

function autoMap(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const header of headers) {
    const alias = FIELD_ALIASES[normalizeHeader(header)];
    if (alias) mapping[header] = alias;
  }
  return mapping;
}

const MODE_LABEL: Record<GoogleSheetSyncMode, string> = {
  new_only: 'New rows only',
  full: 'Every row, every sync',
};

/** Deep-links straight to the tab, not just the file — `#gid=` is what makes
 *  "open in Google Sheets" land on the sheet the row is actually about. */
function sheetUrl(spreadsheetId: string, sheetTabId?: number): string {
  const base = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
  return sheetTabId === undefined ? base : `${base}#gid=${sheetTabId}`;
}

interface Workbook {
  spreadsheetId: string;
  spreadsheetName: string;
  tabs: GoogleSheetSource[];
}

/** Sources arrive flat and creation-ordered. Grouping preserves that order
 *  both for the workbooks and for the tabs inside each, so a sheet never
 *  jumps position between polls. */
function groupByWorkbook(sources: GoogleSheetSource[]): Workbook[] {
  const order: string[] = [];
  const map = new Map<string, Workbook>();
  for (const source of sources) {
    let book = map.get(source.spreadsheetId);
    if (!book) {
      book = {
        spreadsheetId: source.spreadsheetId,
        spreadsheetName: source.spreadsheetName || 'Untitled spreadsheet',
        tabs: [],
      };
      map.set(source.spreadsheetId, book);
      order.push(source.spreadsheetId);
    }
    book.tabs.push(source);
  }
  return order.map((id) => map.get(id)!);
}

interface Props {
  /** Matches the notice state Integrations.tsx already owns, so both connector
   *  cards surface their results through the one banner at the top of the page
   *  rather than each growing an alert of its own. */
  onNotice: (notice: { severity: 'success' | 'error'; message: string }) => void;
}

const GoogleSheetsConnector: React.FC<Props> = ({ onNotice }) => {
  const { terminology } = useTerminology();
  const location = useLocation();
  const [status, setStatus] = useState<GoogleSheetsConnectorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [busySourceId, setBusySourceId] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [presetWorkbook, setPresetWorkbook] = useState<Workbook | null>(null);
  const [polling, setPolling] = useState(false);
  const { openPicker } = useGooglePicker();

  /** Keeps polling alive for a moment after a manual sync. A job that was just
   *  enqueued has usually not written its `running` row yet, so deciding purely
   *  on what the response contains would leave the card frozen until the tenant
   *  reloaded — the exact moment they are watching it most closely. */
  const graceUntilRef = useRef(0);

  const fetchStatus = useCallback(async () => {
    try {
      const next = await adminApi.getGoogleSheetsStatus();
      setStatus(next);
      const anyRunning =
        next.connected && next.recentRuns.some((run) => run.status === 'running');
      // Deciding here rather than in the effect means `polling` only flips on a
      // real transition, so exactly one interval is created per active period
      // instead of one torn down and rebuilt on every tick.
      setPolling(anyRunning || Date.now() < graceUntilRef.current);
    } catch (err) {
      console.error('Error fetching Google Sheets status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const sources = useMemo(() => status?.sources ?? [], [status]);
  const recentRuns = useMemo(
    () => (status?.connected ? status.recentRuns : []),
    [status]
  );

  /** Which sheets are mid-read right now. Drives both the per-tab progress bar
   *  and whether this card polls at all. */
  const runningSourceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const run of recentRuns) {
      if (run.status === 'running' && run.sourceId) ids.add(run.sourceId);
    }
    return ids;
  }, [recentRuns]);

  // Poll only while something is genuinely moving, and stop the moment it
  // settles — an idle connector polled every four seconds is pure load for a
  // number that changes twice a day. `polling` is set by fetchStatus above, so
  // this effect re-runs on a transition and never on every tick.
  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => void fetchStatus(), CONNECTOR_POLL_MS);
    return () => window.clearInterval(id);
  }, [polling, fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { url } = await adminApi.getGoogleSheetsAuthorizeUrl();
      window.location.href = url;
    } catch (err: any) {
      setConnecting(false);
      onNotice({
        severity: 'error',
        message:
          err?.response?.data?.error?.message ||
          'Google Sheets is not set up on this deployment yet. Ask your administrator to add the Google app credentials.',
      });
    }
  };

  const handleConfirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await adminApi.disconnectGoogleSheets();
      setDisconnectOpen(false);
      onNotice({
        severity: 'success',
        message: 'Google disconnected. Your sheet setups are kept, so reconnecting restores them.',
      });
      await fetchStatus();
    } catch {
      onNotice({ severity: 'error', message: 'Could not disconnect. Please try again.' });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async (source: GoogleSheetSource, full: boolean) => {
    setBusySourceId(source._id);
    try {
      await adminApi.triggerGoogleSheetSync(source._id, full);
      // Watch for long enough that a queued job has time to start and report.
      graceUntilRef.current = Date.now() + 30_000;
      setPolling(true);
      onNotice({
        severity: 'success',
        message: full
          ? `Re-reading every row of “${source.sheetTabName}”.`
          : `Checking “${source.sheetTabName}” for new rows.`,
      });
      await fetchStatus();
    } catch (err: any) {
      onNotice({
        severity: 'error',
        message: err?.response?.data?.error?.message || 'Could not start that sync.',
      });
    } finally {
      setBusySourceId(null);
    }
  };

  const handleRemove = async (source: GoogleSheetSource) => {
    setBusySourceId(source._id);
    try {
      await adminApi.deleteGoogleSheetSource(source._id);
      onNotice({
        severity: 'success',
        message: `Stopped reading “${source.sheetTabName}”. ${terminology.lead.plural} already imported are untouched.`,
      });
      await fetchStatus();
    } catch {
      onNotice({ severity: 'error', message: 'Could not remove that sheet.' });
    } finally {
      setBusySourceId(null);
    }
  };

  const handleResume = async (source: GoogleSheetSource) => {
    setBusySourceId(source._id);
    try {
      await adminApi.updateGoogleSheetSource(source._id, { status: 'active' });
      await fetchStatus();
    } catch {
      onNotice({ severity: 'error', message: 'Could not resume that sheet.' });
    } finally {
      setBusySourceId(null);
    }
  };

  const workbooks = useMemo(() => groupByWorkbook(sources), [sources]);
  const leadWord = terminology.lead.plural.toLowerCase();
  // "1 enquiries added" is the kind of small wrongness that makes a product
  // feel unfinished, and tenants can rename this noun freely.
  const leadWordSingular = terminology.lead.singular.toLowerCase();

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
        <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </CardContent>
      </Card>
    );
  }

  const connected = status?.connected === true;

  return (
    <>
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
        <CardContent sx={{ p: 3 }}>
          {!connected ? (
            <>
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <GoogleSheetsIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Google Sheets
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={3} maxWidth={560}>
                Read {leadWord} straight out of a Google Sheet — including the sheet Meta writes
                your Lead Ads into. You choose which spreadsheet to share and which tab to read,
                and Baatly only ever reads: nothing is written back into your sheet.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                startIcon={
                  connecting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <GoogleSheetsIcon htmlColor="#fff" />
                  )
                }
                onClick={handleConnect}
                disabled={connecting}
                sx={{
                  bgcolor: GOOGLE_SHEETS_GREEN,
                  '&:hover': { bgcolor: GOOGLE_SHEETS_GREEN_DARK },
                  borderRadius: 1.5,
                  px: 2.5,
                }}
              >
                {connecting ? 'Redirecting to Google…' : 'Connect Google Sheets'}
              </Button>
            </>
          ) : (
            <>
              {/* Header — identity, quiet status, account, actions. Same shape
                  and order as the HubSpot card directly above it. */}
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <GoogleSheetsIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Google Sheets
                </Typography>
                <StatusPill status={status.status === 'disconnected' ? 'error' : status.status} />
                {status.accountEmail && (
                  <Tooltip
                    title={`Connected ${new Date(status.connectedAt).toLocaleString()}`}
                    placement="top"
                    arrow
                  >
                    <Typography variant="body2" color="text.secondary">
                      {status.accountEmail}
                    </Typography>
                  </Tooltip>
                )}
                <Box flex={1} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => {
                    setPresetWorkbook(null);
                    setAddOpen(true);
                  }}
                  disabled={status.status !== 'connected'}
                  sx={{ borderRadius: 1.5 }}
                >
                  Add a sheet
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<LinkOff />}
                  onClick={() => setDisconnectOpen(true)}
                  sx={{ borderRadius: 1.5 }}
                >
                  Disconnect
                </Button>
              </Box>

              {status.status === 'reauth_required' && (
                <Alert severity="warning" sx={{ mt: 2 }} icon={<ErrorOutline />}>
                  Google access expired or was revoked — every sheet below is paused until you
                  reconnect.{' '}
                  <Button size="small" onClick={handleConnect} disabled={connecting}>
                    Reconnect
                  </Button>
                </Alert>
              )}

              <Divider sx={{ my: 2.5 }} />

              {workbooks.length === 0 ? (
                <Box textAlign="center" py={3}>
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    No sheets yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2} maxWidth={420} mx="auto">
                    Add one to choose a spreadsheet, pick which tab holds your {leadWord}, and
                    match its columns.
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<Add />}
                    onClick={() => {
                      setPresetWorkbook(null);
                      setAddOpen(true);
                    }}
                    sx={{
                      bgcolor: GOOGLE_SHEETS_GREEN,
                      '&:hover': { bgcolor: GOOGLE_SHEETS_GREEN_DARK },
                      borderRadius: 1.5,
                    }}
                  >
                    Add a sheet
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {workbooks.map((book) => (
                    <Box key={book.spreadsheetId}>
                      {/* Workbook header — the file, with its tabs beneath. */}
                      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {book.spreadsheetName}
                        </Typography>
                        <Chip
                          label={`${book.tabs.length} tab${book.tabs.length === 1 ? '' : 's'} connected`}
                          size="small"
                          sx={{ height: 20, fontSize: 11, fontWeight: 600, border: 0, bgcolor: 'action.hover' }}
                        />
                        <MuiLink
                          href={sheetUrl(book.spreadsheetId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.4,
                            fontSize: 12,
                            textDecoration: 'none',
                          }}
                        >
                          Open in Google Sheets <OpenInNew sx={{ fontSize: 13 }} />
                        </MuiLink>
                        <Box flex={1} />
                        {/* The sub-sheet affordance. A workbook almost always
                            has tabs beyond the connected one, and this is what
                            makes them reachable instead of invisible. */}
                        <Button
                          size="small"
                          startIcon={<Add />}
                          disabled={status.status !== 'connected'}
                          onClick={() => {
                            setPresetWorkbook(book);
                            setAddOpen(true);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Add another tab
                        </Button>
                      </Box>

                      <Stack
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                        }}
                      >
                        {book.tabs.map((source, index) => (
                          <SheetRow
                            key={source._id}
                            source={source}
                            runs={recentRuns.filter((r) => r.sourceId === source._id)}
                            isRunning={runningSourceIds.has(source._id)}
                            busy={busySourceId === source._id}
                            connectionUsable={status.status === 'connected'}
                            leadWord={leadWord}
                            leadWordSingular={leadWordSingular}
                            leadsHref={`${location.pathname.startsWith('/admin') ? '/admin' : ''}/activity/leads`}
                            divided={index > 0}
                            onSync={handleSync}
                            onRemove={handleRemove}
                            onResume={handleResume}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={disconnectOpen} onClose={() => !disconnecting && setDisconnectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect Google Sheets?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            Baatly stops reading your sheets immediately. {terminology.lead.plural} already
            imported stay exactly as they are, and your sheet setups are kept — reconnecting the
            same Google account restores every tab and mapping.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisconnectOpen(false)} disabled={disconnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDisconnect}
            disabled={disconnecting}
            startIcon={disconnecting ? <CircularProgress size={16} color="inherit" /> : <LinkOff />}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddSheetDialog
        open={addOpen}
        preset={presetWorkbook}
        // The account the SERVER holds a grant for. The Picker opens on it, so
        // a browser signed in to several Google accounts cannot quietly hand
        // back a file this deployment has no access to.
        accountEmail={status?.connected ? status.accountEmail : undefined}
        onClose={() => setAddOpen(false)}
        openPicker={openPicker}
        onNotice={onNotice}
        onCreated={async () => {
          setAddOpen(false);
          // createSource enqueues the first read straight away — watch for it
          // rather than showing a new sheet that looks inert.
          graceUntilRef.current = Date.now() + 30_000;
          setPolling(true);
          await fetchStatus();
        }}
      />
    </>
  );
};

/**
 * One connected tab. The default surface is a single plain-language state
 * line; counts, the row cursor and the run history live behind "View sync
 * details", so a healthy sheet reads as one calm sentence.
 */
const SheetRow: React.FC<{
  source: GoogleSheetSource;
  runs: ConnectorSyncRun[];
  isRunning: boolean;
  busy: boolean;
  connectionUsable: boolean;
  leadWord: string;
  leadWordSingular: string;
  leadsHref: string;
  divided: boolean;
  onSync: (source: GoogleSheetSource, full: boolean) => void;
  onRemove: (source: GoogleSheetSource) => void;
  onResume: (source: GoogleSheetSource) => void;
}> = ({
  source,
  runs,
  isRunning,
  busy,
  connectionUsable,
  leadWord,
  leadWordSingular,
  leadsHref,
  divided,
  onSync,
  onRemove,
  onResume,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const errored = source.status === 'error';
  const neverSynced = !source.lastSyncAt;
  /** Falls back to the lifetime total only for sources that synced before
   *  `lastRun` existed — without that, every pre-existing sheet would report
   *  "no new rows" on a check that genuinely imported something. */
  const lastAdded = source.lastRun ? source.lastRun.created : source.counts.created;
  /** Reads "checked automatically" only when we actually know it was. A source
   *  synced before `lastRun` existed says nothing rather than guessing. */
  const lastCheckWasAutomatic = source.lastRun?.trigger === 'scheduled';

  return (
    <Box
      sx={{
        p: 2,
        borderTop: divided ? '1px solid' : 'none',
        borderColor: 'divider',
        bgcolor: errored ? 'rgba(183,121,31,0.04)' : 'transparent',
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box flex={1} minWidth={220}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {source.sheetTabName || 'Untitled tab'}
            </Typography>
            <Chip
              label={MODE_LABEL[source.mode]}
              size="small"
              sx={{
                height: 19,
                fontSize: 10.5,
                fontWeight: 600,
                border: 0,
                bgcolor: 'action.hover',
                color: 'text.secondary',
              }}
            />
            <MuiLink
              href={sheetUrl(source.spreadsheetId, source.sheetTabId)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}
              aria-label={`Open the ${source.sheetTabName} tab in Google Sheets`}
            >
              <OpenInNew sx={{ fontSize: 13 }} />
            </MuiLink>
          </Box>

          {/* The one state line. Order matters: a sheet that is BOTH errored
              and mid-read should show the error, since that is the thing a
              person can act on. */}
          {errored ? (
            <Alert
              severity="warning"
              icon={<ErrorOutline fontSize="inherit" />}
              sx={{ py: 0.25, mt: 0.5 }}
              action={
                <Button size="small" onClick={() => onResume(source)} disabled={busy}>
                  Resume
                </Button>
              }
            >
              {source.lastErrorMessage || 'This sheet is paused.'}
            </Alert>
          ) : isRunning ? (
            <Box maxWidth={360}>
              <Typography variant="body2" color="text.secondary" mb={0.75}>
                Reading rows…
              </Typography>
              <LinearProgress variant="indeterminate" sx={{ borderRadius: 1, height: 5 }} />
            </Box>
          ) : neverSynced ? (
            <Typography variant="body2" color="text.secondary">
              Waiting for the first read…
            </Typography>
          ) : (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <CheckCircle sx={{ color: '#1B8A5A', fontSize: 18, mt: 0.25 }} />
              <Box>
                {/* Two numbers, because one cannot answer both questions a
                    tenant actually has. `lastRun` is what the most recent check
                    did; `counts` is the lifetime total. Showing only the total
                    beside "last checked just now" implied the whole total had
                    ARRIVED just now — which for an idle sheet, the normal state
                    between polls, was simply untrue. */}
                <Typography variant="body2">
                  <strong>Up to date</strong> —{' '}
                  {lastAdded > 0 ? (
                    <>
                      {formatCount(lastAdded)} {lastAdded === 1 ? leadWordSingular : leadWord} added{' '}
                      {timeAgo(source.lastSyncAt)}
                    </>
                  ) : (
                    <>
                      no new rows, last checked
                      {lastCheckWasAutomatic ? ' automatically ' : ' '}
                      {timeAgo(source.lastSyncAt)}
                    </>
                  )}
                  {source.counts.created > 0 && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' · '}
                      {formatCount(source.counts.created)} in total
                    </Typography>
                  )}
                </Typography>
                {source.counts.created > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Rows from this tab appear as {leadWord}.{' '}
                    <MuiLink component={RouterLink} to={leadsHref} sx={{ verticalAlign: 'baseline' }}>
                      View {leadWord}
                    </MuiLink>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={0.75} flexShrink={0}>
          <Tooltip title="Check for new rows now">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={busy ? <CircularProgress size={13} color="inherit" /> : <Sync />}
                disabled={busy || errored || !connectionUsable}
                onClick={() => onSync(source, false)}
                sx={{ borderRadius: 1.5, textTransform: 'none' }}
              >
                Sync now
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Read every row again from the top. Safe — existing leads merge rather than duplicate.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Replay />}
                disabled={busy || !connectionUsable}
                onClick={() => onSync(source, true)}
                sx={{ borderRadius: 1.5, textTransform: 'none' }}
              >
                Re-read all
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Stop reading this tab">
            <span>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={busy}
                onClick={() => onRemove(source)}
                sx={{ borderRadius: 1.5, minWidth: 0, px: 1 }}
                aria-label={`Stop reading ${source.sheetTabName}`}
              >
                <DeleteOutline fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Button
        size="small"
        onClick={() => setDetailsOpen((v) => !v)}
        endIcon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
        sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
      >
        {detailsOpen ? 'Hide sync details' : 'View sync details'}
      </Button>

      <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
        <Box mt={1} pt={2} borderTop="1px solid" borderColor="divider">
          <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
            <Detail label="Read up to row" value={formatCount(source.cursor.lastRowIndex)} />
            <Detail
              label="Last check"
              value={lastAdded > 0 ? `+${formatCount(lastAdded)}` : 'nothing new'}
              hint={`What the most recent sync added, ${timeAgo(source.lastSyncAt)}.`}
            />
            <Detail label={`${leadWord} added in total`} value={formatCount(source.counts.created)} />
            <Detail label="Updated" value={formatCount(source.counts.updated)} />
            {source.counts.skipped > 0 && (
              <Detail
                label="Skipped"
                value={formatCount(source.counts.skipped)}
                hint="Rows with no phone number and no email — a lead can't be created without one."
              />
            )}
            <Detail label="Header row" value={String(source.headerRow)} />
            <Detail label="Columns mapped" value={String(Object.keys(source.mapping || {}).length)} />
          </Box>

          {/* Naming what this list omits is the whole point. Automatic checks
              run continuously and are discarded when they find nothing — 96 a
              day per sheet would bury the runs that matter — so a tenant
              looking at a recent "last checked" timestamp above a list of only
              their own manual runs would otherwise conclude the schedule had
              stopped. It has not; the timestamp above is the proof. */}
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Baatly checks this sheet automatically. Only checks that added or changed something are
            listed below
            {source.lastSyncAt && (
              <> — the last check was {timeAgo(source.lastSyncAt)}</>
            )}
            .
          </Typography>

          {runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sync has changed anything for this tab yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {runs.slice(0, 5).map((run) => (
                <ListItem
                  key={run._id}
                  disableGutters
                  sx={{ py: 0.75, px: 1.25, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                          label={TRIGGER_LABEL[run.trigger] || run.trigger}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 600,
                            border: 0,
                            ...TRIGGER_CHIP_SX[run.trigger],
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {run.counts.created} new, {run.counts.updated} updated
                          {run.counts.failed ? `, ${run.counts.failed} failed` : ''}
                        </Typography>
                        {run.status === 'failed' && (
                          <Chip label="Failed" size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
                        )}
                        {run.status === 'running' && (
                          <Chip label="Running" size="small" sx={{ height: 20, fontSize: 11 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Tooltip title={new Date(run.startedAt).toLocaleString()} placement="top" arrow>
                        <Typography variant="caption" color="text.secondary">
                          {timeAgo(run.startedAt)}
                          {run.errorSummary ? ` · ${run.errorSummary}` : ''}
                        </Typography>
                      </Tooltip>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const Detail: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'capitalize' }}>
      {label}
    </Typography>
    {hint ? (
      <Tooltip title={hint} placement="bottom-start" arrow>
        <Typography variant="body2" sx={{ borderBottom: '1px dotted', borderColor: 'divider', display: 'inline-block' }}>
          {value}
        </Typography>
      </Tooltip>
    ) : (
      <Typography variant="body2">{value}</Typography>
    )}
  </Box>
);

/**
 * Pick a spreadsheet, pick a TAB, map its columns.
 *
 * The tab step is not optional and has no default. Assuming the first sheet is
 * exactly how the file uploader on the Leads page loses a workbook's other
 * tabs without ever saying so, and this connector exists partly to not repeat
 * that.
 *
 * When opened from "Add another tab", the workbook is already known, so the
 * Picker step is skipped entirely and the dialog opens straight onto the tab
 * list for that file.
 */
const AddSheetDialog: React.FC<{
  open: boolean;
  preset: Workbook | null;
  /** `| undefined` is explicit because this project runs
   *  exactOptionalPropertyTypes: the parent genuinely passes undefined when no
   *  account is connected, which a bare `?:` would reject. */
  accountEmail?: string | undefined;
  onClose: () => void;
  openPicker: ReturnType<typeof useGooglePicker>['openPicker'];
  onNotice: Props['onNotice'];
  onCreated: () => void;
}> = ({ open, preset, accountEmail, onClose, openPicker, onNotice, onCreated }) => {
  const [picked, setPicked] = useState<{ spreadsheetId: string; name: string } | null>(null);
  const [tabs, setTabs] = useState<GoogleSheetTab[]>([]);
  const [tabId, setTabId] = useState<number | ''>('');
  const [headerRow, setHeaderRow] = useState(1);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<string[][]>([]);
  const [lastColumn, setLastColumn] = useState('Z');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<GoogleSheetSyncMode>('new_only');
  const [step, setStep] = useState<'pick' | 'tab' | 'map'>('pick');
  const [working, setWorking] = useState(false);
  /** True while Google's Picker owns the screen. This dialog then hides itself
   *  rather than closing: closing would trip the reset effect below and throw
   *  away the workbook the user is in the middle of choosing. */
  const [pickerOpen, setPickerOpen] = useState(false);

  /** Tabs of this workbook already connected — shown as disabled in the list
   *  with a reason, rather than silently missing. A tenant who cannot find the
   *  tab they wanted deserves to be told it is already set up. */
  const takenTabIds = useMemo(
    () => new Set((preset?.tabs || []).map((t) => t.sheetTabId)),
    [preset]
  );

  const loadTabsFor = useCallback(
    async (spreadsheetId: string) => {
      const found = await adminApi.listGoogleSheetTabs(spreadsheetId);
      setTabs(found);
      setStep('tab');
    },
    []
  );

  useEffect(() => {
    if (!open) {
      setPicked(null);
      setTabs([]);
      setTabId('');
      setHeaderRow(1);
      setHeaders([]);
      setSampleRows([]);
      setMapping({});
      setMode('new_only');
      setStep('pick');
      return;
    }
    if (!preset) return;

    // Opened from "Add another tab" — the file is known, so skip the Picker.
    setPicked({ spreadsheetId: preset.spreadsheetId, name: preset.spreadsheetName });
    setWorking(true);
    loadTabsFor(preset.spreadsheetId)
      .catch((err: any) =>
        onNotice({
          severity: 'error',
          message: err?.response?.data?.error?.message || 'Could not list that spreadsheet’s tabs.',
        })
      )
      .finally(() => setWorking(false));
  }, [open, preset, loadTabsFor, onNotice]);

  const handlePick = async () => {
    setWorking(true);
    try {
      const { clientId, pickerApiKey } = await adminApi.getGoogleSheetsAuthorizeUrl();
      if (!clientId || !pickerApiKey) {
        onNotice({
          severity: 'error',
          message:
            'The Google Picker is not set up on this deployment. Ask your administrator to add the Picker API key.',
        });
        return;
      }

      // Step aside for the whole time Google's dialog is up. Two stacked
      // modals is the wrong shape regardless of which one wins on z-index,
      // and leaving this one on top left a dead "Opening…" spinner covering
      // the file list the user was trying to click.
      setPickerOpen(true);
      let result: Awaited<ReturnType<typeof openPicker>>;
      try {
        result = await openPicker({ clientId, apiKey: pickerApiKey, accountEmail });
      } finally {
        // Always comes back, including on a cancel or a throw — otherwise the
        // dialog would stay hidden with nothing on screen to recover it.
        setPickerOpen(false);
      }
      if (!result) return; // cancelled — an ordinary outcome, say nothing

      setPicked(result);
      await loadTabsFor(result.spreadsheetId);
    } catch (err: any) {
      const status = err?.response?.status;
      onNotice({
        severity: 'error',
        message:
          status === 404 || status === 403
            ? `Baatly can't open that spreadsheet. Make sure you picked it while signed in as ${
                accountEmail || 'the connected Google account'
              } — a file chosen from a different account can't be read.`
            : err?.response?.data?.error?.message || 'Could not open your Google Drive.',
      });
    } finally {
      setWorking(false);
    }
  };

  const loadPreview = useCallback(
    async (selectedTabId: number, row: number) => {
      if (!picked) return;
      setWorking(true);
      try {
        const preview = await adminApi.previewGoogleSheetTab({
          spreadsheetId: picked.spreadsheetId,
          sheetTabId: selectedTabId,
          headerRow: row,
        });
        setHeaders(preview.headers);
        setSampleRows(preview.sampleRows);
        setLastColumn(preview.lastColumn);
        setMapping(autoMap(preview.headers));
        setStep('map');
      } catch (err: any) {
        onNotice({
          severity: 'error',
          message: err?.response?.data?.error?.message || 'Could not read that tab.',
        });
      } finally {
        setWorking(false);
      }
    },
    [picked, onNotice]
  );

  /**
   * A lead is identified by phone OR email — that is the dedupe key
   * bulkUpsertLeads merges on, and rows carrying neither are skipped outright.
   * So this is a genuine precondition, not a preference, and the UI owes the
   * tenant a reason rather than just a dead button.
   */
  const identity = useMemo(() => {
    const targets = new Set(Object.values(mapping));
    const phone = targets.has('customerPhone');
    const email = targets.has('customerEmail');
    return { phone, email, ok: phone || email };
  }, [mapping]);
  const hasIdentity = identity.ok;

  /** True when NOTHING in this tab even resembles contact data. Then the
   *  honest advice is "you may have the wrong tab", not "keep mapping" — a
   *  task tracker has no phone column to find however hard someone looks. */
  const noContactColumnAvailable = useMemo(() => {
    if (headers.length === 0) return false;
    return !headers.some((header) => {
      const alias = FIELD_ALIASES[normalizeHeader(header)];
      return alias === 'customerPhone' || alias === 'customerEmail';
    });
  }, [headers]);

  /** Named once and reused by the banner and the button's tooltip, so the
   *  blocker is worded identically wherever the tenant meets it. */
  const blockingReason = hasIdentity
    ? null
    : 'Map a column to Phone or Email first — that is how a lead is identified.';

  const mappedCount = useMemo(
    () => Object.values(mapping).filter(Boolean).length,
    [mapping]
  );

  const handleCreate = async () => {
    if (!picked || tabId === '') return;
    const tab = tabs.find((t) => t.sheetTabId === tabId);
    setWorking(true);
    try {
      await adminApi.createGoogleSheetSource({
        spreadsheetId: picked.spreadsheetId,
        spreadsheetName: picked.name,
        sheetTabId: Number(tabId),
        sheetTabName: tab?.title || '',
        headerRow,
        lastColumn,
        mapping,
        mode,
      });
      onNotice({
        severity: 'success',
        message: `Now reading “${tab?.title}”. The first sync has started.`,
      });
      onCreated();
    } catch (err: any) {
      onNotice({
        severity: 'error',
        message: err?.response?.data?.error?.message || 'Could not add that sheet.',
      });
    } finally {
      setWorking(false);
    }
  };

  const stepLabel =
    step === 'pick' ? 'Choose a spreadsheet' : step === 'tab' ? 'Choose a tab' : 'Match your columns';

  return (
    <Dialog
      open={open}
      onClose={() => !working && onClose()}
      maxWidth="md"
      fullWidth
      // Hidden, never unmounted — see `pickerOpen`. `display: none` also takes
      // the backdrop out of the way, so the Picker underneath becomes fully
      // clickable.
      sx={{ ...(pickerOpen ? { display: 'none' } : {}) }}
      // MUI keeps focus inside an open Modal. The Picker mounts OUTSIDE this
      // dialog's DOM, so without this the trap yanks focus back and its search
      // box cannot be typed into — invisible, and far more confusing than the
      // z-index problem it sits behind.
      disableEnforceFocus={pickerOpen}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <GoogleSheetsIcon sx={{ fontSize: 22 }} />
          <span>Add a sheet</span>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Step {step === 'pick' ? 1 : step === 'tab' ? 2 : 3} of 3 · {stepLabel}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {step === 'pick' && (
          <Box py={2}>
            <Typography variant="body2" color="text.secondary" mb={2} maxWidth={520}>
              Baatly gets access only to the file you pick here — nothing else in your Drive — and
              only ever reads from it.
            </Typography>
            <Button
              variant="contained"
              disableElevation
              onClick={handlePick}
              disabled={working}
              startIcon={working ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                bgcolor: GOOGLE_SHEETS_GREEN,
                '&:hover': { bgcolor: GOOGLE_SHEETS_GREEN_DARK },
                borderRadius: 1.5,
              }}
            >
              {working ? 'Opening…' : 'Choose a spreadsheet'}
            </Button>
          </Box>
        )}

        {step === 'tab' && (
          <Box py={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              <strong>{picked?.name}</strong> has {tabs.length} tab{tabs.length === 1 ? '' : 's'}.
              Choose the one holding your leads — each tab is set up separately, so you can add the
              others afterwards too.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Tab</InputLabel>
                <Select label="Tab" value={tabId} onChange={(e) => setTabId(Number(e.target.value))}>
                  {tabs.map((t) => {
                    const taken = takenTabIds.has(t.sheetTabId);
                    return (
                      <MenuItem key={t.sheetTabId} value={t.sheetTabId} disabled={taken}>
                        {t.title}
                        {taken && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            already connected
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Header row"
                value={headerRow}
                onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))}
                sx={{ width: 130 }}
                helperText="Usually 1"
              />
              <Button
                variant="contained"
                disableElevation
                disabled={tabId === '' || working}
                onClick={() => loadPreview(Number(tabId), headerRow)}
                sx={{ mt: 0.25, borderRadius: 1.5 }}
              >
                {working ? 'Reading…' : 'Continue'}
              </Button>
            </Stack>
          </Box>
        )}

        {step === 'map' && (
          <Box py={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" maxWidth={520}>
                Match your columns to lead fields. Anything you don't need can stay on “Don't
                import”, and anything without a matching field can become a custom field.
              </Typography>
              {/* Deliberately never green. "4 of 4 mapped" beside a disabled
                  button is the exact contradiction that sends someone hunting
                  for a bug: every column IS mapped, and the sheet still can't
                  be used. The count stays factual and quiet; the banner below
                  is what carries whether this is actually usable. */}
              <Chip
                label={`${mappedCount} of ${headers.length} columns mapped`}
                size="small"
                sx={{ fontWeight: 600, bgcolor: 'action.hover', border: 0 }}
              />
            </Box>

            <FormControl size="small" sx={{ minWidth: 320, mb: 2 }}>
              <InputLabel>What should each sync read?</InputLabel>
              <Select
                label="What should each sync read?"
                value={mode}
                onChange={(e) => setMode(e.target.value as GoogleSheetSyncMode)}
              >
                <MenuItem value="new_only">Only rows added since the last sync</MenuItem>
                <MenuItem value="full">Every row, every sync</MenuItem>
              </Select>
            </FormControl>

            {/* Always rendered, in one of two states, rather than appearing
                only when unmet. A requirement that vanishes once satisfied
                leaves the tenant unsure whether it was met or simply forgotten
                — and this one also carries genuinely useful information after
                the fact, since it names the field leads will dedupe on. */}
            {hasIdentity ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Ready — leads from this tab will be matched on{' '}
                <strong>
                  {identity.phone && identity.email
                    ? 'phone and email'
                    : identity.phone
                      ? 'phone number'
                      : 'email address'}
                </strong>
                , so re-reading a row updates the same lead instead of creating a duplicate.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Map a column to Phone or Email to continue.</strong> A lead is identified
                by one of them, so rows carrying neither can't be imported.
                {noContactColumnAvailable && (
                  <>
                    {' '}
                    None of this tab's columns look like a phone number or an email address — you
                    may want a different tab.
                  </>
                )}
              </Alert>
            )}

            <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Example</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Import as</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header, index) => {
                    const current = mapping[header] || '';
                    const isCustom = current.startsWith(CUSTOM_FIELD_MAPPING_PREFIX);
                    const selectValue = isCustom ? CUSTOM_FIELD_SELECT_VALUE : current;
                    return (
                      <TableRow key={header || `col-${index}`} hover>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {header || <em>(no header)</em>}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'text.secondary',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {sampleRows[0]?.[index] || '—'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Select
                              size="small"
                              value={selectValue}
                              sx={{ minWidth: 180 }}
                              onChange={(e) => {
                                const value = String(e.target.value);
                                setMapping((prev) => {
                                  const next = { ...prev };
                                  if (value === '') delete next[header];
                                  else if (value === CUSTOM_FIELD_SELECT_VALUE) {
                                    next[header] = `${CUSTOM_FIELD_MAPPING_PREFIX}${header}`;
                                  } else next[header] = value;
                                  return next;
                                });
                              }}
                            >
                              {TARGET_FIELD_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {isCustom && (
                              <TextField
                                size="small"
                                label="Field name"
                                value={current.slice(CUSTOM_FIELD_MAPPING_PREFIX.length)}
                                onChange={(e) =>
                                  setMapping((prev) => ({
                                    ...prev,
                                    [header]: `${CUSTOM_FIELD_MAPPING_PREFIX}${e.target.value}`,
                                  }))
                                }
                                sx={{ width: 170 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'map' && (
          <Button onClick={() => setStep('tab')} disabled={working} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={working}>
          Cancel
        </Button>
        {step === 'map' && (
          // A disabled control fires no mouse events, so its tooltip needs a
          // wrapper element that does — the same span pattern the sheet-row
          // action buttons already use. Without it the explanation simply
          // never appears, which is how a disabled button ends up looking
          // broken rather than blocked.
          <Tooltip title={blockingReason ?? ''} placement="top" arrow>
            <span>
              <Button
                variant="contained"
                disableElevation
                onClick={handleCreate}
                disabled={working || !hasIdentity}
                sx={{
                  bgcolor: GOOGLE_SHEETS_GREEN,
                  '&:hover': { bgcolor: GOOGLE_SHEETS_GREEN_DARK },
                  borderRadius: 1.5,
                }}
              >
                {working ? 'Adding…' : 'Start reading this sheet'}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleSheetsConnector;
